/**
 * CR140: Schedule History Dialog
 * CR140.1: Enhanced run history with config snapshot drawer and error display
 * CR140.2: Timezone clarity - show (ET) suffix and UTC debug tooltips
 * CR161: Add "View Diagnostics" link when forum_diagnostics_triggered is true
 */

import { useState } from "react";
import { ExternalLink, RefreshCw, Eye, AlertCircle, MessageSquare } from "lucide-react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useScheduleRuns } from "@/hooks/useScheduleRuns";
import type { ScheduleWithMerchants, ScheduleRunStatus, BatchRunScheduleRun } from "@/lib/scheduler/types";
import { 
  formatScheduleTime, 
  formatScheduleDate,
  getUtcDebugString 
} from "@/lib/scheduler/timezoneUtils";

interface ScheduleHistoryProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schedule: ScheduleWithMerchants | null;
}

function getStatusBadge(status: ScheduleRunStatus) {
  const variants: Record<ScheduleRunStatus, { variant: "default" | "secondary" | "destructive" | "outline"; label: string }> = {
    PENDING: { variant: "outline", label: "Pending" },
    DISPATCHED: { variant: "secondary", label: "Running" },
    SUCCESS: { variant: "default", label: "Success" },
    ERROR: { variant: "destructive", label: "Error" },
    CANCELLED: { variant: "outline", label: "Cancelled" },
  };
  
  const config = variants[status];
  return <Badge variant={config.variant}>{config.label}</Badge>;
}

export function ScheduleHistory({
  open,
  onOpenChange,
  schedule,
}: ScheduleHistoryProps) {
  const { runs, loading, refetch } = useScheduleRuns(schedule?.id || null);
  const [configSnapshot, setConfigSnapshot] = useState<{ run: BatchRunScheduleRun; json: Record<string, unknown> } | null>(null);

  const handleViewConfig = (run: BatchRunScheduleRun) => {
    setConfigSnapshot({ run, json: run.config_snapshot });
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-5xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>
              Run History: {schedule?.name || "Schedule"}
            </DialogTitle>
            <DialogDescription>
              View all scheduled runs and their batch run results.
            </DialogDescription>
          </DialogHeader>

          <div className="flex justify-end mb-2">
            <Button variant="outline" size="sm" onClick={refetch}>
              <RefreshCw className="h-4 w-4 mr-1" />
              Refresh
            </Button>
          </div>

          <div className="flex-1 overflow-auto border rounded-lg">
            {loading ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Scheduled For</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Expected</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Progress</TableHead>
                    <TableHead>Error</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[1, 2, 3].map(i => (
                    <TableRow key={i}>
                      <TableCell><Skeleton className="h-5 w-28" /></TableCell>
                      <TableCell><Skeleton className="h-5 w-20" /></TableCell>
                      <TableCell><Skeleton className="h-5 w-12" /></TableCell>
                      <TableCell><Skeleton className="h-5 w-12" /></TableCell>
                      <TableCell><Skeleton className="h-5 w-32" /></TableCell>
                      <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                      <TableCell><Skeleton className="h-8 w-28" /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : runs.length === 0 ? (
              <div className="p-12 text-center text-muted-foreground">
                No runs yet for this schedule.
              </div>
            ) : (
              <TooltipProvider>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Scheduled For</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Expected</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Progress</TableHead>
                      <TableHead>Error</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {runs.map(run => {
                      const displayDate = formatScheduleDate(run.scheduled_for);
                      const displayTime = formatScheduleTime(run.scheduled_for, { includeDate: false, includeTimezone: true });
                      const utcDebug = getUtcDebugString(run.scheduled_for);
                      
                      return (
                        <TableRow key={run.id}>
                          <TableCell>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex flex-col cursor-help">
                                  <span className="font-medium">{displayDate}</span>
                                  <span className="text-xs text-muted-foreground">{displayTime}</span>
                                </div>
                              </TooltipTrigger>
                              <TooltipContent side="right" className="text-xs">
                                <p>{utcDebug}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TableCell>
                        <TableCell>{getStatusBadge(run.status)}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{run.expected_run_count}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{run.created_run_count}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-0.5 text-sm">
                            <div className="flex items-center gap-2">
                              <span className="text-muted-foreground">
                                {run.runs_completed + run.runs_failed} / {run.created_run_count}
                              </span>
                            </div>
                            <div className="flex gap-2 text-xs">
                              <span className="text-muted-foreground">
                                ✓ {run.runs_completed}
                              </span>
                              {run.runs_failed > 0 && (
                                <span className="text-destructive">
                                  ✕ {run.runs_failed}
                                </span>
                              )}
                              {run.runs_enqueued > 0 && run.runs_enqueued !== run.created_run_count && (
                                <span className="text-muted-foreground">
                                  ⏳ {run.runs_enqueued - run.runs_started}
                                </span>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {run.error_message ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex items-center gap-1 text-destructive cursor-help">
                                  <AlertCircle className="h-4 w-4" />
                                  <span className="text-xs max-w-24 truncate">
                                    {run.error_message.slice(0, 30)}...
                                  </span>
                                </div>
                              </TooltipTrigger>
                              <TooltipContent side="left" className="max-w-sm">
                                <p className="text-xs break-words whitespace-pre-wrap">
                                  {run.error_message}
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                            <span className="text-muted-foreground text-xs">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              asChild
                            >
                              <Link to={`/admin/scheduler/runs/${run.id}`}>
                                Review
                                <ExternalLink className="h-3 w-3 ml-1" />
                              </Link>
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              asChild
                            >
                              <Link to={`/admin/batch-runs?schedule_run_id=${run.id}`}>
                                View Runs
                                <ExternalLink className="h-3 w-3 ml-1" />
                              </Link>
                            </Button>
                            {/* CR161: View Diagnostics link when forum was triggered */}
                            {run.forum_diagnostics_triggered && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    asChild
                                    disabled={!run.forum_diagnostics_run_id}
                                  >
                                    {run.forum_diagnostics_run_id ? (
                                      <Link to={`/admin/batch-runs/${run.forum_diagnostics_run_id}`}>
                                        <MessageSquare className="h-4 w-4 mr-1" />
                                        Diagnostics
                                      </Link>
                                    ) : (
                                      <span className="flex items-center">
                                        <MessageSquare className="h-4 w-4 mr-1" />
                                        Diagnostics
                                      </span>
                                    )}
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="text-xs">
                                    {run.forum_diagnostics_run_id 
                                      ? "View Forum Diagnostics Run Details" 
                                      : "Diagnostics run not available yet"}
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleViewConfig(run)}
                              title="View Config Snapshot"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  </TableBody>
                </Table>
              </TooltipProvider>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Config Snapshot Sheet */}
      <Sheet open={!!configSnapshot} onOpenChange={() => setConfigSnapshot(null)}>
        <SheetContent className="w-[500px] sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>Config Snapshot</SheetTitle>
            <SheetDescription>
              Configuration used for run at {configSnapshot && formatScheduleTime(configSnapshot.run.scheduled_for, { includeTimezone: true })}
            </SheetDescription>
          </SheetHeader>
          <ScrollArea className="h-[calc(100vh-120px)] mt-4">
            <pre className="text-xs bg-muted p-4 rounded-lg overflow-auto whitespace-pre-wrap">
              {configSnapshot && JSON.stringify(configSnapshot.json, null, 2)}
            </pre>
          </ScrollArea>
        </SheetContent>
      </Sheet>
    </>
  );
}
